<template>
  <div class="root-timetable timetable">
    <table v-resize="debouncedDoLayout" :style="tableStyle">
      <thead>
        <tr>
          <th class="col-head row-head"></th>
          <slot
            v-for="(col, i) in columns"
            name="column-head"
            :column="col"
            :index="i"
            :timetable="this"
          >
            <th class="col-head" :key="i" :style="{ width: `calc(90% / ${columns.length})` }">
              {{ col }}
            </th>
          </slot>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(time, ri) in generateTimeRows" :key="ri">
          <slot name="row-head" :time="time" :index="ri" :timetable="this">
            <td class="row-head">{{ time | timeRange }}</td>
          </slot>
          <td v-for="(col, ci) in columns" :key="ci" :ref="`cell-${ci}-${ri}`">
            <slot
              name="cell-background"
              :time="time"
              :column="col"
              :cell="{ col: ci, top: ri, bottom: ri }"
              :timetable="this"
            >
              <div class="cell-background">
                &nbsp;
                <!-- this is a placeholder to reserve grid space for the cell.
               you should set a fixed height to this element -->
              </div>
            </slot>
          </td>
        </tr>
      </tbody>
    </table>
    <slot name="footer"> </slot>
    <slot
      v-if="showCurrentTime && isToday_ && cellsLayout && typeof currentTimeYPos_ === 'number'"
      name="currentTime"
      :offsetTop="currentTimeYPos_"
      :now="now"
    >
      <div
        class="currentTime"
        :style="{
          top: currentTimeYPos_ + 'px',
          left: 0,
          right: 0,
        }"
      ></div>
    </slot>
    <div class="events-container" v-if="cellsLayout">
      <div
        v-for="ev in displayEvents_"
        :key="ev._id"
        style="position: absolute; pointer-events: none"
        :style="getCellStyles(getEventCell(ev))"
      >
        <slot name="cell-event" :event="ev" :cell="getEventCell(ev)" :timetable="this" />
      </div>
    </div>
  </div>
</template>

<script>
import { throttle } from 'lodash';
import moment from 'moment';

const Duration = moment.duration().constructor;
const Moment = moment().constructor;

export default {
  name: 'Timetable',
  props: {
    tableStyle: {},
    /**
     * current display date
     */
    date: [Date, Moment],
    /**
     * chairs
     */
    columns: { type: Array, default: () => [] },
    /**
     * column comparator
     */
    columnKey: { type: [String, Function], default: '_id' },
    // start of day
    minTime: { type: [Number, Duration, Object], default: 0 },
    // end of day
    maxTime: { type: [Number, Duration, Object], default: () => moment.duration({ day: 1 }) },
    gridInterval: {
      type: [Number, Duration, Object],
      default: () => moment.duration({ minutes: 30 }),
    },
    showCurrentTime: [Boolean, Date],
    /** @type {{ column: *, start: Date, end: Date }[]} */
    events: { type: Array, default: () => [] },
  },
  data() {
    return {
      /** @type {{ [ref: string]: { left: number, top: number, width: number, height: number }}} */
      cellsLayout: null,
      debouncedDoLayout: throttle(() => this.$nextTick(this.doLayout_), 200, { trailing: true }),
      now: new Date(),
      nowInterval_: null,
    };
  },
  beforeDestroy() {
    clearInterval(this.nowInterval_);
  },
  watch: {
    showCurrentTime: {
      immediate: true,
      handler(v) {
        if (v === true) {
          this.now = new Date();
          this.nowInterval_ = setInterval(() => {
            this.now = new Date();
          }, 30000);
        } else if (v) {
          this.now = v;
        } else {
          clearInterval(this.nowInterval_);
        }
      },
    },
    doLayoutWatcher_: {
      immediate: true,
      handler() {
        this.debouncedDoLayout();
      },
    },
    columns() {
      this.debouncedDoLayout();
    },
  },
  computed: {
    //region cached computations
    minTime_() {
      return +moment.duration(this.minTime);
    },
    maxTime_() {
      return +moment.duration(this.maxTime);
    },
    gridInterval_() {
      return +moment.duration(this.gridInterval);
    },
    maxRows_() {
      return Math.trunc((this.maxTime_ - this.minTime_) / this.gridInterval_);
    },
    displayEvents_() {
      return this.events.filter(ev => this.columnsMap_.has(this.columnKey_(ev.column)));
    },
    columnsMap_() {
      return new Map(this.columns.map(x => [this.columnKey_(x), x]));
    },
    isToday_() {
      return moment(this.date).isSame(this.now, 'day');
    },
    currentTimeYPos_() {
      const top = this.getRowOffsetByTime(this.now);
      const startCell = this.cellsLayout[this.getCellRef(0, top)];
      if (!startCell) return null;
      return startCell.top + (top - Math.trunc(top)) * startCell.height;
    },
    currentDateZeroOffset() {
      return moment(this.date).startOf('day').valueOf();
    },
    //endregion
    //region watch aggregators
    doLayoutWatcher_() {
      return JSON.stringify([this.maxRows_, this.columns.length]);
    },
    //endregion
    generateTimeRows() {
      const date = moment(this.date).startOf('day');
      const { minTime_, maxTime_, gridInterval_ } = this;
      const arr = [];
      for (let offset = minTime_; offset < maxTime_; offset += gridInterval_) {
        arr.push({
          start: date.clone().add(offset),
          startOffset: offset,
          end: date.clone().add(offset).add(gridInterval_),
          endOffset: offset + gridInterval_,
        });
      }
      return arr;
    },
    /** @type {Set<string>} */
    cellsOccupied() {
      const refs = new Set();
      for (const ev of this.displayEvents_) {
        const { col, top, bottom } = this.getEventCell(ev);
        refs.add(this.getCellRef(col, top));
        refs.add(this.getCellRef(col, bottom));
      }
      return refs;
    },
  },
  methods: {
    //region internal
    /**
     * measure cell boundaries
     * @private
     */
    doLayout_() {
      const map = {};
      for (let x = this.columns.length - 1; x >= 0; --x) {
        for (let y = this.maxRows_ - 1; y >= 0; --y) {
          const ref = this.getCellRef(x, y);
          const el = this.$refs[ref]?.[0];
          map[ref] = this.measureElement(el);
        }
      }
      this.cellsLayout = map;
    },
    measureElement($el) {
      return (
        $el && {
          left: $el.offsetLeft,
          top: $el.offsetTop,
          width: $el.offsetWidth,
          height: $el.offsetHeight,
        }
      );
    },
    columnKey_(column) {
      return typeof this.columnKey === 'function'
        ? this.columnKey(column)
        : column?.[this.columnKey] || column;
    },
    findColumn_(column) {
      return this.columnsMap_.get(this.columnKey_(column));
    },
    //endregion
    /**
     * compute grid row Y pixel offset
     * @param time
     * @return {number}
     */
    getRowOffsetByTime(time) {
      const v = moment(time);
      const offset = v - this.currentDateZeroOffset;
      const x = (offset - this.minTime_) / this.gridInterval_;
      return parseFloat(x.toFixed(5));
    },
    /**
     * compute cell boundary of event
     * @param column
     * @param {Date} start inclusive
     * @param {Date} end exclusive
     * @return {{col: number, top: number, bottom: number}}
     */
    getEventCell({ column, start, end }) {
      return {
        col: this.columns.indexOf(this.findColumn_(column)),
        top: Math.max(0, this.getRowOffsetByTime(start)),
        bottom: Math.min(
          this.maxRows_ - 0.00001, // upper bound is exclusive
          this.getRowOffsetByTime(
            moment(end)
              // end time is exclusive. subtract one ms to make the calculation correct
              .add(-1, 'ms'),
          ),
        ),
      };
    },
    /**
     * get the $ref key from a cell xy
     * @param x
     * @param y
     * @return {string}
     */
    getCellRef(x, y) {
      return `cell-${Math.trunc(x)}-${Math.trunc(y)}`;
    },
    /**
     * generate css style for cell
     * @param col
     * @param top inclusive
     * @param bottom inclusive
     */
    getCellStyles({ col, top, bottom }) {
      const startCell = this.cellsLayout[this.getCellRef(col, top)];
      let endCell = this.cellsLayout[this.getCellRef(col, bottom)];
      if (startCell && endCell) {
        const partialTop = startCell.top + (top - Math.trunc(top)) * startCell.height;
        const partialBottom = endCell.top + (bottom - Math.trunc(bottom)) * endCell.height;
        return {
          left: startCell.left + 'px',
          top: partialTop + 'px',
          width: startCell.width + 'px',
          height: partialBottom - partialTop + 'px',
        };
      }
      return null;
    },
    /**
     * @param col
     * @param top inclusive
     * @param bottom inclusive
     * @return {boolean}
     */
    isRangeEmpty(col, top, bottom = top) {
      const cells = this.cellsOccupied;
      if (top === bottom) {
        return cells.has(this.getCellRef(col, top));
      } else {
        for (let i = top; i <= bottom; ++i) {
          if (cells.has(this.getCellRef(col, i))) {
            return false;
          }
        }
        return true;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.root-timetable {
  position: relative;

  table {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;

    .col-head {
      border-bottom: 1px solid silver;

      &.row-head {
        border-bottom: 0;
        border-right: 0;
      }
    }

    .row-head {
      border-right: 1px solid silver;
    }
  }

  .currentTime {
    position: absolute;
    border-top: 2px solid red;
  }
}
</style>
